/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Spinner,
} from 'reactstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { api } from '../../../config';
import Swal from 'sweetalert2';
import Select from 'react-select';
import './UserFormModal.css';

/* ── Icon helpers (Remix Icon class names) ── */
const IC = {
  user: 'ri-user-3-line',
  mail: 'ri-mail-line',
  lock: 'ri-lock-line',
  phone: 'ri-phone-line',
  brief: 'ri-briefcase-2-line',
  home: 'ri-home-4-line',
  shield: 'ri-shield-keyhole-line',
  camera: 'ri-camera-fill',
  eye: 'ri-eye-fill',
  eyeOff: 'ri-eye-off-fill',
  close: 'ri-close-line',
  save: 'ri-check-line',
  person: 'ri-user-add-line',
  id: 'ri-profile-line',
  building: 'ri-building-2-line',
  file: 'ri-file-text-line',
  group: 'ri-group-line',
};

const SECTIONS = [
  { id: 'personal', label: 'Personal', icon: IC.user },
  { id: 'employment', label: 'Employment', icon: IC.brief },
  { id: 'contact', label: 'Contact', icon: IC.phone },
  { id: 'security', label: 'Security', icon: IC.shield },
];

const Field = ({ label, required, icon, children, col = 6 }) => (
  <Col sm={col}>
    <div className='ufm-field'>
      <Label className='ufm-label'>
        <i className={`${icon} ufm-label-icon`} />
        {label}
        {required && <span className='ufm-required'>*</span>}
      </Label>
      {children}
    </div>
  </Col>
);

const UserFormModal = ({ isOpen, toggle, userData, onSuccess }) => {
  const token = useSelector(state => state.Login?.user?.token);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    userName: '',
    password: '',
    photo: '',
    employeeNo: '',
    jobTitle: '',
    division: '',
    employeeType: '',
    phoneNumber: '',
    address: '',
    roles: [],
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const isAddMode = !userData;

  /* ── Password strength ── */
  const getPasswordStrength = useCallback(() => {
    const p = formData.password;
    if (!p) return { score: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[!@#$%^&*]/.test(p)) score++;
    const map = {
      1: { label: 'Weak', color: '#ef4444' },
      2: { label: 'Fair', color: '#f59e0b' },
      3: { label: 'Good', color: '#3b82f6' },
      4: { label: 'Strong', color: '#10b981' },
    };
    return { score, ...map[score] };
  }, [formData.password]);

  const strength = getPasswordStrength();

  /* ── Fetch roles ── */
  const fetchRoles = async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/role?PageNumber=1&PageSize=200`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = (result.data?.items || []).map(item => ({
        value: item.name,
        label: item.name,
      }));
      setRoleOptions(items);
      return items;
    } catch (error) {
      console.error('Error fetchRoles:', error);
      return [];
    }
  };

  useEffect(() => {
    if (isOpen) fetchRoles();
  }, [isOpen]);

  useEffect(() => {
    if (!userData) {
      setFormData({
        firstName: '',
        lastName: '',
        displayName: '',
        email: '',
        userName: '',
        password: '',
        photo: '',
        employeeNo: '',
        jobTitle: '',
        division: '',
        employeeType: '',
        phoneNumber: '',
        address: '',
        roles: [],
      });
      setPreview(null);
      setSelectedFile(null);
      setErrors({});
    } else {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        displayName: userData.displayName || '',
        email: userData.email || '',
        userName: userData.userName || '',
        password: '',
        photo: userData.photo || '',
        employeeNo: userData.employeeNo || '',
        jobTitle: userData.jobTitle || '',
        division: userData.division || '',
        employeeType: userData.employeeType || '',
        phoneNumber: userData.phoneNumber || '',
        address: userData.address || '',
        roles: userData.roles || [],
      });
      setPreview(userData.photo || null);
      setSelectedFile(null);
      setErrors({});
    }
    setActiveSection('personal');
  }, [userData, isOpen]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleRolesChange = selected => {
    setFormData(prev => ({
      ...prev,
      roles: selected ? selected.map(o => o.value) : [],
    }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please upload an image file', 'error');
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.displayName.trim())
      newErrors.displayName = 'Display name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = 'Email is invalid';
    if (isAddMode) {
      if (!formData.userName.trim())
        newErrors.userName = 'Username is required';
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8)
        newErrors.password = 'Minimum 8 characters';
      else if (!/[A-Z]/.test(formData.password))
        newErrors.password = 'Must have uppercase';
      else if (!/[!@#$]/.test(formData.password))
        newErrors.password = 'Must have symbol';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { photo, ...payload } = formData;
      if (isAddMode) {
        payload.userName = formData.userName;
        payload.password = formData.password;
      }

      let targetId = isAddMode ? null : userData.id;

      if (isAddMode) {
        const res = await axios.post(`${api.API_URL}/appuser`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        targetId = res.data?.data?.id || res.data?.id || res.data;
        Swal.fire({
          icon: 'success',
          title: 'User Created',
          text: 'New user has been added successfully',
          confirmButtonColor: '#6366f1',
        });
      } else {
        await axios.put(`${api.API_URL}/appuser/${userData.id}`, payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        Swal.fire({
          icon: 'success',
          title: 'Profile Updated',
          text: 'User profile has been updated successfully',
          confirmButtonColor: '#6366f1',
        });
      }

      if (selectedFile && targetId) {
        const photoFormData = new FormData();
        photoFormData.append('file', selectedFile);
        try {
          await axios.post(
            `${api.API_URL}/appuser/${targetId}/upload-picture`,
            photoFormData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        } catch (uploadError) {
          console.error(
            'Gagal upload foto, tapi user berhasil disimpan',
            uploadError
          );
        }
      }

      onSuccess?.();
      toggle();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.message || 'Failed to save',
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderSectionNav = () => (
    <div className='ufm-section-nav'>
      {SECTIONS.map(s => (
        <button
          key={s.id}
          type='button'
          className={`ufm-section-btn ${activeSection === s.id ? 'active' : ''}`}
          onClick={() => setActiveSection(s.id)}
        >
          <i className={s.icon} />
          <span>{s.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size='xl'
      className='ufm-modal'
      backdrop='static'
    >
      {/* ── Header ── */}
      <div className='ufm-header'>
        <div className='ufm-header-left'>
          <div className='ufm-header-icon'>
            <i className={isAddMode ? IC.person : IC.user} />
          </div>
          <div>
            <h3 className='ufm-title'>
              {isAddMode ? 'Add New User' : 'Edit User Profile'}
            </h3>
            <p className='ufm-subtitle'>
              {isAddMode
                ? 'Fill in the details to create a new user account'
                : 'Update user information and settings'}
            </p>
          </div>
        </div>
        <button className='ufm-close-btn' onClick={toggle} type='button'>
          <i className={IC.close} />
        </button>
      </div>

      <ModalBody className='ufm-body'>
        <div className='ufm-layout'>
          {/* ── Left: Avatar + Section Nav ── */}
          <div className='ufm-sidebar'>
            {/* Avatar */}
            <div className='ufm-avatar-section'>
              <div className='ufm-avatar-wrapper'>
                <div className='ufm-avatar'>
                  {preview ? (
                    <img src={preview} alt='Profile' />
                  ) : (
                    <div className='ufm-avatar-placeholder'>
                      <i className='ri-user-line' />
                    </div>
                  )}
                  <Label
                    htmlFor='profile-img-file-input'
                    className='ufm-avatar-overlay'
                  >
                    <i className={IC.camera} />
                    <span>Change</span>
                  </Label>
                </div>
                <Input
                  type='file'
                  id='profile-img-file-input'
                  className='d-none'
                  accept='image/png, image/jpeg, image/jpg'
                  onChange={handleFileChange}
                />
              </div>
              <p className='ufm-avatar-hint'>JPG or PNG, max 2MB</p>
              {formData.displayName && (
                <p className='ufm-avatar-name'>{formData.displayName}</p>
              )}
              {formData.email && (
                <p className='ufm-avatar-email'>{formData.email}</p>
              )}
            </div>

            {/* Section navigation */}
            {renderSectionNav()}
          </div>

          {/* ── Right: Form fields ── */}
          <div className='ufm-content'>
            {/* Personal Section */}
            <div
              className={`ufm-section ${activeSection === 'personal' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.user} />
                <div>
                  <h5>Personal Information</h5>
                  <p>Basic identity and name details</p>
                </div>
              </div>
              <Row className='g-3'>
                <Field label='First Name' required icon={IC.user}>
                  <Input
                    type='text'
                    name='firstName'
                    value={formData.firstName}
                    onChange={handleChange}
                    invalid={!!errors.firstName}
                    placeholder='Enter first name'
                  />
                  <FormFeedback>{errors.firstName}</FormFeedback>
                </Field>
                <Field label='Last Name' required icon={IC.user}>
                  <Input
                    type='text'
                    name='lastName'
                    value={formData.lastName}
                    onChange={handleChange}
                    invalid={!!errors.lastName}
                    placeholder='Enter last name'
                  />
                  <FormFeedback>{errors.lastName}</FormFeedback>
                </Field>
                <Field label='Display Name' required icon={IC.id}>
                  <Input
                    type='text'
                    name='displayName'
                    value={formData.displayName}
                    onChange={handleChange}
                    invalid={!!errors.displayName}
                    placeholder='How name appears publicly'
                  />
                  <FormFeedback>{errors.displayName}</FormFeedback>
                </Field>
                <Field label='Email' required icon={IC.mail}>
                  <Input
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    invalid={!!errors.email}
                    placeholder='user@company.com'
                  />
                  <FormFeedback>{errors.email}</FormFeedback>
                </Field>
              </Row>
            </div>

            {/* Employment Section */}
            <div
              className={`ufm-section ${activeSection === 'employment' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.brief} />
                <div>
                  <h5>Employment Details</h5>
                  <p>Job-related information and role assignment</p>
                </div>
              </div>
              <Row className='g-3'>
                <Field label='Employee No' icon={IC.id}>
                  <Input
                    type='text'
                    name='employeeNo'
                    value={formData.employeeNo}
                    onChange={handleChange}
                    placeholder='e.g. EMP-00123'
                  />
                </Field>
                <Field label='Job Title' icon={IC.brief}>
                  <Input
                    type='text'
                    name='jobTitle'
                    value={formData.jobTitle}
                    onChange={handleChange}
                    placeholder='e.g. Software Engineer'
                  />
                </Field>
                <Field label='Division' icon={IC.building}>
                  <Input
                    type='text'
                    name='division'
                    value={formData.division}
                    onChange={handleChange}
                    placeholder='e.g. Engineering'
                  />
                </Field>
                <Field label='Employee Type' icon={IC.file}>
                  <Input
                    type='text'
                    name='employeeType'
                    value={formData.employeeType}
                    onChange={handleChange}
                    placeholder='e.g. Full-time, Contract'
                  />
                </Field>
                <Field label='Roles' icon={IC.shield} col={12}>
                  <Select
                    isMulti
                    placeholder='Select roles...'
                    options={roleOptions}
                    value={
                      Array.isArray(roleOptions)
                        ? roleOptions.filter(o =>
                            formData.roles.includes(o.value)
                          )
                        : []
                    }
                    onChange={handleRolesChange}
                    className='ufm-select'
                    classNamePrefix='ufm'
                    noOptionsMessage={() => 'No roles available'}
                  />
                </Field>
              </Row>
            </div>

            {/* Contact Section */}
            <div
              className={`ufm-section ${activeSection === 'contact' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.phone} />
                <div>
                  <h5>Contact Information</h5>
                  <p>Phone number and address details</p>
                </div>
              </div>
              <Row className='g-3'>
                <Field label='Phone Number' icon={IC.phone}>
                  <Input
                    type='text'
                    name='phoneNumber'
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder='+62 812 3456 7890'
                  />
                </Field>
                <Field label='Address' icon={IC.home} col={12}>
                  <textarea
                    className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                    name='address'
                    rows={3}
                    value={formData.address}
                    onChange={handleChange}
                    placeholder='Full mailing address'
                  />
                </Field>
              </Row>
            </div>

            {/* Security Section */}
            <div
              className={`ufm-section ${activeSection === 'security' ? 'active' : ''}`}
            >
              <div className='ufm-section-header'>
                <i className={IC.shield} />
                <div>
                  <h5>Security Credentials</h5>
                  <p>
                    Account access and authentication{' '}
                    {isAddMode ? '(required)' : '(leave blank to keep current)'}
                  </p>
                </div>
              </div>
              <Row className='g-3'>
                {isAddMode && (
                  <>
                    <Field label='Username' required icon={IC.user}>
                      <Input
                        type='text'
                        name='userName'
                        value={formData.userName}
                        onChange={handleChange}
                        invalid={!!errors.userName}
                        placeholder='Unique username'
                      />
                      <FormFeedback>{errors.userName}</FormFeedback>
                    </Field>
                    <Field label='Password' required icon={IC.lock}>
                      <div className='ufm-password-wrap'>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          name='password'
                          value={formData.password}
                          onChange={handleChange}
                          invalid={!!errors.password}
                          placeholder='Min 8 chars, uppercase & symbol'
                        />
                        <button
                          type='button'
                          className='ufm-eye-btn'
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={showPassword ? IC.eyeOff : IC.eye} />
                        </button>
                      </div>
                      <FormFeedback>{errors.password}</FormFeedback>
                      {/* Strength meter */}
                      {formData.password && (
                        <div className='ufm-strength'>
                          <div className='ufm-strength-bar'>
                            <div
                              className='ufm-strength-fill'
                              style={{
                                width: `${strength.score * 25}%`,
                                background: strength.color,
                              }}
                            />
                          </div>
                          <span
                            className='ufm-strength-label'
                            style={{ color: strength.color }}
                          >
                            {strength.label}
                          </span>
                        </div>
                      )}
                      {/* Checklist */}
                      {formData.password && (
                        <ul className='ufm-pw-checklist'>
                          <li
                            className={
                              formData.password.length >= 8 ? 'pass' : ''
                            }
                          >
                            <i
                              className={
                                formData.password.length >= 8
                                  ? 'ri-check-line'
                                  : 'ri-close-line'
                              }
                            />
                            At least 8 characters
                          </li>
                          <li
                            className={
                              /[A-Z]/.test(formData.password) ? 'pass' : ''
                            }
                          >
                            <i
                              className={
                                /[A-Z]/.test(formData.password)
                                  ? 'ri-check-line'
                                  : 'ri-close-line'
                              }
                            />
                            One uppercase letter
                          </li>
                          <li
                            className={
                              /[0-9]/.test(formData.password) ? 'pass' : ''
                            }
                          >
                            <i
                              className={
                                /[0-9]/.test(formData.password)
                                  ? 'ri-check-line'
                                  : 'ri-close-line'
                              }
                            />
                            One number
                          </li>
                          <li
                            className={
                              /[!@#$%^&*]/.test(formData.password) ? 'pass' : ''
                            }
                          >
                            <i
                              className={
                                /[!@#$%^&*]/.test(formData.password)
                                  ? 'ri-check-line'
                                  : 'ri-close-line'
                              }
                            />
                            One special character
                          </li>
                        </ul>
                      )}
                    </Field>
                  </>
                )}
                {!isAddMode && (
                  <Col sm={12}>
                    <div className='ufm-readonly-notice'>
                      <i className='ri-information-line' />
                      <div>
                        <h6>Password unchanged</h6>
                        <p>
                          Password can be changed separately through the
                          password reset feature.
                        </p>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </ModalBody>

      {/* ── Footer ── */}
      <div className='ufm-footer'>
        <Button className='ufm-btn ufm-btn-ghost' onClick={toggle}>
          <i className='ri-close-line' />
          Cancel
        </Button>
        <Button
          className='ufm-btn ufm-btn-primary'
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner size='sm' className='me-1' />
              Processing...
            </>
          ) : (
            <>
              <i className={IC.save} />
              {isAddMode ? 'Create User' : 'Save Changes'}
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
};

export default UserFormModal;
